import { Divider, Grid, Label, Loader } from "@aws-amplify/ui-react";
import { CCard } from "@coreui/react";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { API, graphqlOperation } from "aws-amplify";

import { listEquipment, listMaintenanceRoutines } from "graphql/queries";
import ActivitiesInfo from "./ActivitiesInfo";
import { OrderContext } from "components/serviceOrders/context/OrderContext";

const MaintenanceRoutinesInfo = ({
  maintenanceRoutineIDs,
  equipmentRoutines,
}) => {
  const { onChangeActivity } = useContext(OrderContext);
  const [maintenanceRoutines, setMaintenanceRoutines] = useState({
    data: [],
    loading: false,
  });
  const [equipments, setEquipments] = useState({
    data: [],
    loading: false,
  });

  const getEquipments = async (ids) => {
    try {
      setEquipments({ data: [], loading: true });

      const equipmentsData = await API.graphql(
        graphqlOperation(listEquipment, {
          filter: { or: ids.map((id) => ({ id: { eq: id } })) },
        })
      );
      const equipmentsDataItems = equipmentsData.data.listEquipment.items;

      setEquipments((prev) => ({
        ...prev,
        data: equipmentsDataItems,
      }));
    } catch (err) {
      toast.error(strings.errors.fetchEquipments);
    } finally {
      setEquipments((prev) => ({ ...prev, loading: false }));
    }
  };

  const getMaintenanceRoutineData = async (ids, eqRoutines) => {
    try {
      setMaintenanceRoutines({ data: [], loading: true });

      const idsUnique = [
        ...new Set([
          ...(ids ?? []),
          ...(eqRoutines?.map((eq) => eq.routineID) ?? []),
        ]),
      ];

      const maintenanceRoutineData = await API.graphql(
        graphqlOperation(listMaintenanceRoutines, {
          filter: { or: idsUnique.map((id) => ({ id: { eq: id } })) },
        })
      );
      const maintenanceRoutineDataItems =
        maintenanceRoutineData.data.listMaintenanceRoutines.items;

      setMaintenanceRoutines((prev) => ({
        ...prev,
        data: [
          ...(eqRoutines?.map((eq) => {
            const routineFound = maintenanceRoutineDataItems.find(
              (m) => m.id === eq.routineID
            );
            return {
              ...routineFound,
              equipmentID: eq.equipmentID,
            };
          }) ?? []),
          ...(maintenanceRoutineDataItems.filter((m) =>
            ids?.some((i) => i === m.id)
          ) ?? []),
        ],
      }));
    } catch (err) {
      toast.error(strings.errors.fetchMaintenanceRoutines);
    } finally {
      setMaintenanceRoutines((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    getMaintenanceRoutineData(maintenanceRoutineIDs, equipmentRoutines);
    if (equipmentRoutines?.length > 0)
      getEquipments(equipmentRoutines.map((eq) => eq.equipmentID));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenanceRoutineIDs, equipmentRoutines]);

  return (
    <Grid gap="5px">
      <span className="fs-5">{strings.maintenanceRoutines}</span>
      {maintenanceRoutines.loading ? (
        <Loader />
      ) : (
        <Grid gap="20px">
          {maintenanceRoutines.data.length > 0
            ? maintenanceRoutines.data.map((maintenanceRoutine) => {
                const equipment = maintenanceRoutine.equipmentID ? (
                  equipments.loading ? (
                    <Loader />
                  ) : (
                    equipments.data?.find(
                      (eq) => eq.id === maintenanceRoutine.equipmentID
                    )?.name ?? ""
                  )
                ) : null;
                return (
                  <CCard
                    borderRadius="medium"
                    variation="outlined"
                    style={{ height: "100%" }}
                  >
                    <Grid gap="5px" templateColumns={{ large: "5fr 0fr 19fr" }}>
                      <Grid
                        className="p-3"
                        gap="25px"
                        templateColumns={{ base: "1fr" }}
                        style={{ alignSelf: "start" }}
                      >
                        {maintenanceRoutine.equipmentID && (
                          <Grid>
                            <Label>{strings.equipment}</Label>
                            <span className="text-muted">{equipment}</span>
                          </Grid>
                        )}
                        <Grid>
                          <Label>{strings.name}</Label>
                          <span className="text-muted">
                            {maintenanceRoutine.name}
                          </span>
                        </Grid>
                        <Grid>
                          <Label>{strings.description}</Label>
                          <span className="text-muted">
                            {maintenanceRoutine.description}
                          </span>
                        </Grid>
                      </Grid>
                      <Divider size="small" orientation="vertical" />
                      <Grid
                        className="p-3"
                        gap="25px"
                        templateColumns={{ base: "1fr" }}
                        style={{ alignSelf: "start" }}
                      >
                        <ActivitiesInfo
                          maintenanceRoutineID={maintenanceRoutine.id}
                          equipmentID={maintenanceRoutine.equipmentID}
                          onChangeActivity={
                            onChangeActivity &&
                            ((actId, actVal) =>
                              onChangeActivity(
                                maintenanceRoutine.id,
                                actId,
                                actVal,
                                maintenanceRoutine.equipmentID
                              ))
                          }
                        />
                      </Grid>
                    </Grid>
                  </CCard>
                );
              })
            : strings.noMaintenanceRoutines}
        </Grid>
      )}
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    maintenanceRoutines: "Rutinas de Mantenimiento",
    equipment: "Equipo",
    name: "Nombre",
    description: "Descripción",
    createdAt: "Fecha de creación",
    errors: {
      fetchMaintenanceRoutines: "Error al consultar las rutinas",
      fetchEquipments: "Error al consultar los equipos",
    },
    noMaintenanceRoutines: "Sin rutinas...",
  },
});

export default MaintenanceRoutinesInfo;
